import moment from "jalali-moment";
import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { DatePicker } from "zaman";
import endpoints from "../../config/endpoints";
import globalEnum from "../../enums/globalEnum";
import useDebounce from "../../hooks/useDebounce";
import { useResponsive } from "../../hooks/useResponsive";
import { TableQueryState } from "../../hooks/useTableQuery";
import restApi from "../../services/restApi";
import { useAppSelector } from "../../services/store";

interface AdvancedFiltersProps {
  query: TableQueryState;
  // The values the date pickers fall back to when the user hasn't picked any.
  defaultDateFrom: string;
  defaultDateTo: string;
  paymentMethod: string;
  paymentMethodCount?: Record<string, { count: number; title: string }>;
}

const paymentMethodTitles = {
  all: "همه",
  card: "کارت به کارت",
  portal: "درگاه",
  credit: "کیف پول",
};

const toIdList = (value?: string) =>
  value ? value.split(",").filter(Boolean) : [];

const AdvancedFilters = ({
  query,
  defaultDateFrom,
  defaultDateTo,
  paymentMethod,
  paymentMethodCount,
}: AdvancedFiltersProps) => {
  const [open, setOpen] = useState(false);
  const [workers, setWorkers] = useState<any[]>([]);
  const { isMobile } = useResponsive();
  const serviceReducer = useAppSelector((state) => state.serviceReducer);

  const { service, workerId, userPhone, dateFrom, dateTo } = query.filters;

  // Local mirror so typing stays responsive; pushed to the URL after a pause.
  const [phoneInput, setPhoneInput] = useState(userPhone || "");
  const debouncedPhone = useDebounce(phoneInput, 400);

  useEffect(() => {
    setPhoneInput(userPhone || "");
  }, [userPhone]);

  useEffect(() => {
    if (debouncedPhone !== (userPhone || "")) {
      query.setFilter("userPhone", debouncedPhone);
    }
  }, [debouncedPhone]);

  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        const res = await restApi(endpoints.user.index, true).get({
          role: globalEnum.roles.WORKER,
        });
        setWorkers(Array.isArray(res?.data) ? res.data : []);
      } catch (error) {
        console.error("Error fetching workers:", error);
      }
    };

    fetchWorkers();
  }, []);

  // Only services that can actually be booked appear on an order, so category
  // nodes are left out — filtering by one could never match an orderService.
  const serviceOptions = useMemo(
    () =>
      (serviceReducer.allServices || [])
        .filter((e: any) => e.price > 0)
        .map((e: any) => ({ value: String(e.id), label: e.title })),
    [serviceReducer.allServices],
  );

  const workerOptions = useMemo(
    () =>
      workers.map((e: any) => ({
        value: String(e.id),
        label: `${e.name || ""} ${e.lastName || ""}`.trim(),
      })),
    [workers],
  );

  const selectStyles = {
    control: (provided: any) => ({
      ...provided,
      minHeight: isMobile ? "44px" : "40px",
      fontSize: isMobile ? "16px" : "14px",
      border: "1px solid #E7E6E5",
      borderRadius: "8px",
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: isMobile ? "8px 12px" : "4px 8px",
      maxHeight: isMobile ? "120px" : "80px",
      overflow: "auto",
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: "#c89dc7",
      color: "white",
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: "white",
      fontSize: isMobile ? "12px" : "11px",
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: "white",
      ":hover": { backgroundColor: "#b388b8", color: "white" },
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#c89dc7"
        : state.isFocused
          ? "#f8f9fa"
          : "white",
      color: state.isSelected ? "white" : "#333",
      fontSize: isMobile ? "14px" : "13px",
    }),
    menu: (provided: any) => ({ ...provided, zIndex: 9999 }),
    indicatorSeparator: () => ({ display: "none" }),
  };

  const activeDateFrom = dateFrom || defaultDateFrom;
  const activeDateTo = dateTo || defaultDateTo;

  const activeFilters = [
    toIdList(service).length > 0,
    toIdList(workerId).length > 0,
    Boolean(userPhone),
    Boolean(dateFrom) && dateFrom !== defaultDateFrom,
    Boolean(dateTo) && dateTo !== defaultDateTo,
    paymentMethod !== "all",
  ].filter(Boolean).length;

  const clearAll = () => {
    setPhoneInput("");
    query.setParams({
      filter_service: "",
      filter_workerId: "",
      filter_userPhone: "",
      filter_dateFrom: "",
      filter_dateTo: "",
      paymentMethod: "",
    });
  };

  return (
    <div className="advFilters">
      <button
        type="button"
        className="advFilters-toggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="advFilters-toggleRight">
          <span className="advFilters-chevron">{open ? "▲" : "▼"}</span>
          <span>فیلترهای پیشرفته</span>
          {activeFilters > 0 && (
            <span className="advFilters-badge">{activeFilters}</span>
          )}
        </span>
        <span className="advFilters-summary">
          از {activeDateFrom} تا {activeDateTo}
        </span>
      </button>

      {open && (
        <div className="advFilters-body">
          <div className="advFilters-field advFilters-fullRow">
            <label>روش پرداخت</label>
            <div className="advFilters-chips">
              {Object.entries(paymentMethodTitles).map(([key, value]) => (
                <span
                  key={key}
                  className={`ordersTag clickable ${key == paymentMethod ? "activeTab" : ""}`}
                  onClick={() => query.setParam("paymentMethod", key)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      query.setParam("paymentMethod", key);
                    }
                  }}
                >
                  {value}
                  {paymentMethodCount && (
                    <span
                      className={`numberTag ${key == paymentMethod ? "activeTab" : ""}`}
                    >
                      {paymentMethodCount[key]?.count || 0}
                    </span>
                  )}
                </span>
              ))}
            </div>
          </div>

          <div className="advFilters-field">
            <label>خدمات</label>
            <Select
              isMulti
              options={serviceOptions}
              value={serviceOptions.filter((o) =>
                toIdList(service).includes(o.value),
              )}
              onChange={(selected: any) =>
                query.setFilter(
                  "service",
                  (selected || []).map((e: any) => e.value).join(","),
                )
              }
              styles={selectStyles}
              placeholder="همه خدمات"
              noOptionsMessage={() => "موردی یافت نشد"}
              className="dirRtl"
            />
          </div>

          <div className="advFilters-field">
            <label>استایلیست</label>
            <Select
              isMulti
              options={workerOptions}
              value={workerOptions.filter((o) =>
                toIdList(workerId).includes(o.value),
              )}
              onChange={(selected: any) =>
                query.setFilter(
                  "workerId",
                  (selected || []).map((e: any) => e.value).join(","),
                )
              }
              styles={selectStyles}
              placeholder="همه استایلیست ها"
              noOptionsMessage={() => "موردی یافت نشد"}
              className="dirRtl"
            />
          </div>

          <div className="advFilters-field">
            <label htmlFor="advFilterPhone">شماره موبایل کاربر</label>
            <input
              id="advFilterPhone"
              className="advFilters-input"
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
              placeholder="۰۹۱۲..."
            />
          </div>

          <div className="advFilters-field">
            <label>از تاریخ</label>
            <DatePicker
              key={`from-${activeDateFrom}`}
              inputClass="advFilters-input"
              defaultValue={moment(activeDateFrom, "jYYYY/jMM/jDD").toDate()}
              onChange={(e: any) =>
                query.setFilter(
                  "dateFrom",
                  moment(e.value.valueOf()).format("jYYYY/jMM/jDD"),
                )
              }
            />
          </div>

          <div className="advFilters-field">
            <label>تا تاریخ</label>
            <DatePicker
              key={`to-${activeDateTo}`}
              inputClass="advFilters-input"
              defaultValue={moment(activeDateTo, "jYYYY/jMM/jDD").toDate()}
              onChange={(e: any) =>
                query.setFilter(
                  "dateTo",
                  moment(e.value.valueOf()).format("jYYYY/jMM/jDD"),
                )
              }
            />
          </div>

          <div className="advFilters-actions">
            <button type="button" className="advFilters-clear" onClick={clearAll}>
              پاک کردن فیلترها
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedFilters;
